import React from 'react'
import { Card, CardHeader, CardContent, Label, Input, Textarea, Button, Badge, Switch } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { getInOutTemplates, setInOutTemplates, type InOutTemplates } from '../services/inOutTemplateService'
import { fetchRoleInOutPolicies, fetchPlanInOutPolicies, upsertRoleInOutPolicy, upsertPlanInOutPolicy } from '../services/inOutAccessService'
import { listPlans } from '@/modules/plans/services/planService'
import type { Plan } from '@/modules/plans/types'

type FieldConfig = {
  key: keyof InOutTemplates
  label: string
  type: 'title' | 'body'
  helper: string
}

const FIELDS: FieldConfig[] = [
  {
    key: 'entryTitle',
    label: 'Entrada no Range - Título',
    type: 'title',
    helper: 'Ex.: {ativo} entrou no range de entrada 💡',
  },
  {
    key: 'entryBody',
    label: 'Entrada no Range - Corpo',
    type: 'body',
    helper:
      'Ex.: O preço de {ativo} entrou no range planejado. Momento ideal para revisar o setup e validar sua execução.',
  },
  {
    key: 'stopTitle',
    label: 'Stop Atingido - Título',
    type: 'title',
    helper: 'Ex.: Stop de {ativo} atingido 🛡️',
  },
  {
    key: 'stopBody',
    label: 'Stop Atingido - Corpo',
    type: 'body',
    helper:
      'Ex.: O stop de {ativo} foi atingido. Não é o cenário ideal, mas faz parte do jogo. Acompanhe novas oportunidades.',
  },
  {
    key: 'tp1Title',
    label: 'Alvo 1 - Título',
    type: 'title',
    helper: 'Ex.: {ativo} atingiu o Alvo 1 💰',
  },
  {
    key: 'tp1Body',
    label: 'Alvo 1 - Corpo',
    type: 'body',
    helper:
      'Ex.: Primeiro alvo alcançado. Movimento alinhado. Avalie parcial e ajuste de stop.',
  },
  {
    key: 'tp2Title',
    label: 'Alvo 2 - Título',
    type: 'title',
    helper: 'Ex.: {ativo} atingiu o Alvo 2 💰💰',
  },
  {
    key: 'tp2Body',
    label: 'Alvo 2 - Corpo',
    type: 'body',
    helper:
      'Ex.: Segundo alvo alcançado. Ótima continuidade. Avalie parcial e ajuste de stop.',
  },
  {
    key: 'tp3Title',
    label: 'Alvo 3 - Título',
    type: 'title',
    helper: 'Ex.: {ativo} atingiu o Alvo 3 💰💰💰',
  },
  {
    key: 'tp3Body',
    label: 'Alvo 3 - Corpo',
    type: 'body',
    helper:
      'Ex.: Terceiro alvo alcançado. Excelente resultado 🔥 Avalie encerrar a posição. Acompanhe novas oportunidades do Cripto Alert.',
  },
  {
    key: 'entryMissedTitle',
    label: 'Entry missed (TP3 sem entry_min) - Título',
    type: 'title',
    helper:
      'Ex.: Ativo Retomou a tendencia sem atingir pontos minimos de entrada.',
  },
  {
    key: 'entryMissedBody',
    label: 'Entry missed (TP3 sem entry_min) - Corpo',
    type: 'body',
    helper:
      'Ex.: {ativo} atingiu TP3 sem tocar o entry_min. O sinal foi valido, mas nao houve ponto minimo de entrada para execucao.',
  },
]

export default function InOutSettings() {
  const { add: toast } = useToast()
  const [tpl, setTpl] = React.useState<InOutTemplates | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)

  const [accessLoading, setAccessLoading] = React.useState(true)
  const [accessSaving, setAccessSaving] = React.useState(false)
  const [rolePolicies, setRolePolicies] = React.useState<Record<string, boolean>>({})
  const [planPolicies, setPlanPolicies] = React.useState<Record<string, boolean>>({})
  const [plans, setPlans] = React.useState<Plan[]>([])

  React.useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const t = await getInOutTemplates()
        if (!alive) return
        setTpl(t)
      } catch (e: any) {
        if (alive) {
          toast({
            title: 'Gerenciar in-out',
            description: e?.message || 'Falha ao carregar templates',
            variant: 'error',
          })
        }
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [toast])

  React.useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        const [roles, plansPolicies, plansResult] = await Promise.all([
          fetchRoleInOutPolicies(),
          fetchPlanInOutPolicies(),
          listPlans({ page: 1, pageSize: 100 }),
        ])
        if (!mounted) return
        const rolesMap: Record<string, boolean> = {}
        roles.forEach((r) => {
          rolesMap[r.role] = !!r.inout_enabled
        })
        const plansMap: Record<string, boolean> = {}
        plansPolicies.forEach((p) => {
          plansMap[p.plan_id] = !!p.inout_enabled
        })
        setRolePolicies(rolesMap)
        setPlanPolicies(plansMap)
        setPlans(plansResult.items)
      } catch (e: any) {
        toast({
          title: 'Gerenciar in-out',
          description: e?.message || 'Falha ao carregar políticas de acesso',
          variant: 'error',
        })
      } finally {
        if (mounted) setAccessLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [toast])

  async function onSave() {
    if (!tpl || saving) return
    setSaving(true)
    try {
      await setInOutTemplates(tpl)
      toast({
        title: 'Gerenciar in-out',
        description: 'Templates salvos com sucesso.',
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Gerenciar in-out',
        description: e?.message || 'Falha ao salvar templates',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  async function onReload() {
    setLoading(true)
    try {
      const t = await getInOutTemplates()
      setTpl(t)
    } catch (e: any) {
      toast({
        title: 'Gerenciar in-out',
        description: e?.message || 'Falha ao recarregar templates',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  async function toggleRole(role: string, next: boolean) {
    if (accessSaving) return
    setAccessSaving(true)
    try {
      await upsertRoleInOutPolicy(role, next)
      setRolePolicies((prev) => ({ ...prev, [role]: next }))
      toast({
        title: 'Gerenciar in-out',
        description: `In-out para role "${role}" ${next ? 'ativado' : 'desativado'}.`,
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Gerenciar in-out',
        description: e?.message || 'Falha ao salvar política por role',
        variant: 'error',
      })
    } finally {
      setAccessSaving(false)
    }
  }

  async function togglePlan(planId: string, next: boolean) {
    if (accessSaving) return
    setAccessSaving(true)
    try {
      await upsertPlanInOutPolicy(planId, next)
      setPlanPolicies((prev) => ({ ...prev, [planId]: next }))
      toast({
        title: 'Gerenciar in-out',
        description: `In-out para plano "${planId}" ${next ? 'ativado' : 'desativado'}.`,
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Gerenciar in-out',
        description: e?.message || 'Falha ao salvar política por plano',
        variant: 'error',
      })
    } finally {
      setAccessSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex items-center justify-between text-sm font-medium">
          <div>Gerenciar in-out (notificações por evento)</div>
          <div className="flex items-center gap-2 text-xs text-foreground/70">
            <Badge>{'{ativo}'}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading || !tpl ? (
            <div className="text-sm text-foreground/70">Carregando…</div>
          ) : (
            <div className="space-y-6">
              <p className="text-xs text-foreground/60">
                Defina os textos padrão das notificações disparadas para usuários que marcaram &quot;Estou dentro&quot;
                em uma oportunidade. Use o placeholder <code>{'{ativo}'}</code> para o símbolo da moeda.
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                {FIELDS.map((field) => {
                  const value = tpl[field.key] ?? ''
                  const commonProps = {
                    value,
                    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setTpl((prev) => ({
                        ...(prev as InOutTemplates),
                        [field.key]: e.target.value,
                      })),
                  }
                  return (
                    <div key={field.key} className="space-y-2">
                      <Label className="block text-xs font-medium">{field.label}</Label>
                      {field.type === 'title' ? (
                        <Input {...commonProps} />
                      ) : (
                        <Textarea rows={3} {...commonProps} />
                      )}
                      <div className="text-[11px] text-foreground/60">{field.helper}</div>
                    </div>
                  )
                })}
              </div>
              <div className="flex gap-2">
                <Button onClick={onSave} disabled={saving}>
                  Salvar
                </Button>
                <Button variant="outline" onClick={onReload} disabled={loading || saving}>
                  Recarregar
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="text-base font-semibold">Acesso ao módulo in-out</CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="text-sm font-medium text-foreground/80">Acesso por papel (role)</div>
            <p className="text-xs text-foreground/60">
              Defina quais papéis podem visualizar o botão &quot;Estou dentro/Estou fora&quot; na página de
              Oportunidades. O acesso efetivo considera role + plano (quando aplicável).
            </p>
            <div className="mt-2 space-y-2">
              {['master', 'member', 'afiliado'].map((role) => (
                <div
                  key={role}
                  className="flex items-center justify-between gap-4 rounded-md border border-border/60 px-3 py-2"
                >
                  <div className="text-sm font-medium capitalize">{role}</div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Desativado</Label>
                    <Switch
                      checked={!!rolePolicies[role]}
                      disabled={accessLoading || accessSaving}
                      onChange={(e) =>
                        toggleRole(role, (e.target as HTMLInputElement).checked)
                      }
                    />
                    <Label className="text-xs">Ativado</Label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium text-foreground/80">Acesso por plano</div>
            <p className="text-xs text-foreground/60">
              Ative o módulo in-out apenas para planos específicos. O acesso efetivo considera role + plano.
            </p>
            <div className="mt-2 space-y-2">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className="flex items-center justify-between gap-4 rounded-md border border-border/60 px-3 py-2"
                >
                  <div className="text-sm font-medium">
                    {plan.name}{' '}
                    <span className="text-xs text-foreground/60">({plan.id})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Desativado</Label>
                    <Switch
                      checked={!!planPolicies[plan.id]}
                      disabled={accessLoading || accessSaving}
                      onChange={(e) =>
                        togglePlan(plan.id, (e.target as HTMLInputElement).checked)
                      }
                    />
                    <Label className="text-xs">Ativado</Label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
